// shared/intake-verdict.ts
//
// Pure weekly-intake verdict: compares a week's average daily intake against the
// (already deficit-adjusted) daily target and returns whether the user is on
// track, under-eating, or over-eating, plus a gentle suggested adjustment to
// daily intake. Intake-based and complementary to the weight-based recalibration
// in functions/lib/recalibrate.ts.

export type IntakeStatus =
  | "on_track"
  | "eating_too_little"
  | "eating_too_much"
  | "not_enough_data";

export interface IntakeVerdict {
  status: IntakeStatus;
  /** avgCalories − target (signed; negative = under target). */
  delta_kcal: number;
  /** Recommended change to daily intake (signed). +ve = eat more, −ve = eat less.
   *  0 when on track or there isn't enough data. */
  suggested_delta_kcal: number;
}

/** Minimum logged days in the week before we'll judge intake. */
const MIN_DAYS = 3;
/** "Significant" deadband: the larger of 150 kcal or 10% of the target. */
const BAND_MIN_KCAL = 150;
const BAND_PCT = 0.10;
/** A suggestion is half the gap, clamped to a gentle range. */
const NUDGE_MIN = 50;
const NUDGE_MAX = 300;

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

export function weeklyIntakeVerdict(args: {
  avgCalories: number;
  target: number;
  daysWithFood: number;
}): IntakeVerdict {
  const { avgCalories, target, daysWithFood } = args;

  if (daysWithFood < MIN_DAYS || target <= 0) {
    return { status: "not_enough_data", delta_kcal: 0, suggested_delta_kcal: 0 };
  }

  const delta = Math.round(avgCalories - target);
  const band = Math.max(BAND_MIN_KCAL, Math.round(target * BAND_PCT));

  if (delta < -band) {
    // Under-eating: nudge intake up by half the gap to avoid under-eating.
    const nudge = clamp(Math.round((target - avgCalories) / 2), NUDGE_MIN, NUDGE_MAX);
    return { status: "eating_too_little", delta_kcal: delta, suggested_delta_kcal: nudge };
  }
  if (delta > band) {
    // Over target: cut intake (or add activity) by half the gap.
    const cut = clamp(Math.round((avgCalories - target) / 2), NUDGE_MIN, NUDGE_MAX);
    return { status: "eating_too_much", delta_kcal: delta, suggested_delta_kcal: -cut };
  }
  return { status: "on_track", delta_kcal: delta, suggested_delta_kcal: 0 };
}
